import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { uploadLeadDocument, getLeadDocuments, deleteLeadDocument, reprocessLeadDocument } from '../../api/documents';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.csv', '.txt', '.md'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const STATUS_BADGES = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  extracting: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Extracting' },
  extracted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Extracted' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Processing' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
};

const ACTIVE_STATUSES = ['pending', 'extracting', 'extracted', 'processing'];

const DocumentsCard = ({ leadId, isCompleted, hasQuestionnaire = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Only track per-row errors from reprocess API calls; processing state comes from doc.processing_status
  const [reprocessErrors, setReprocessErrors] = useState({}); // { [docId]: string }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { can } = usePermissions();
  const pollRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await getLeadDocuments(leadId);
      setDocuments(res.data?.documents || []);
    } catch {
      // Silent
    }
  }, [leadId]);

  useEffect(() => {
    setLoading(true);
    fetchDocuments().finally(() => setLoading(false));
  }, [fetchDocuments]);

  // Poll while any document is in an active processing state
  useEffect(() => {
    const hasActive = documents.some(d => ACTIVE_STATUSES.includes(d.processing_status));
    if (hasActive) {
      pollRef.current = setInterval(fetchDocuments, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [documents, fetchDocuments]);

  const selectableDocs = useMemo(
    () => documents.filter(d => !ACTIVE_STATUSES.includes(d.processing_status)),
    [documents]
  );

  const unprocessedIds = useMemo(
    () => new Set(selectableDocs.filter(d => !d.last_processed_at).map(d => d.id)),
    [selectableDocs]
  );

  const allSelected = selectableDocs.length > 0 && selectedIds.size === selectableDocs.length;
  const unprocessedSelected = unprocessedIds.size > 0 &&
    unprocessedIds.size === selectedIds.size &&
    [...unprocessedIds].every(id => selectedIds.has(id));

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(selectableDocs.map(d => d.id)));
  };

  const handleToggleUnprocessed = () => {
    setSelectedIds(unprocessedSelected ? new Set() : new Set(unprocessedIds));
  };

  const handleToggleRow = (docId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) { next.delete(docId); } else { next.add(docId); }
      return next;
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploading(true);
    try {
      await uploadLeadDocument(leadId, file);
      toast.success('Document uploaded. Processing started.');
      await fetchDocuments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteLeadDocument(leadId, docId);
      toast.success('Document deleted');
      await fetchDocuments();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleReprocess = async (docId, e) => {
    e.stopPropagation();
    setReprocessErrors(s => { const n = { ...s }; delete n[docId]; return n; });
    try {
      await reprocessLeadDocument(leadId, docId);
      // Backend has already set processing_status = PROCESSING; fetch to reflect it and start polling
      await fetchDocuments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reprocess failed';
      setReprocessErrors(s => ({ ...s, [docId]: msg }));
    }
  };

  const handleBulkProcess = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setSelectedIds(new Set());

    const results = await Promise.allSettled(
      ids.map(id => reprocessLeadDocument(leadId, id))
    );

    let queued = 0;
    let failed = 0;
    const errors = {};

    results.forEach((result, i) => {
      const id = ids[i];
      if (result.status === 'fulfilled') {
        queued++;
      } else {
        failed++;
        const msg = result.reason?.response?.data?.detail || 'Reprocess failed';
        errors[id] = msg;
      }
    });

    if (Object.keys(errors).length > 0) {
      setReprocessErrors(s => ({ ...s, ...errors }));
    }

    if (queued > 0) {
      toast.success(`${queued} queued${failed > 0 ? `, ${failed} failed` : ''}`);
      await fetchDocuments(); // Backend set all successful ones to PROCESSING; poll kicks in automatically
    } else {
      toast.error(`${failed} failed`);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatLastProcessed = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-sans font-semibold text-gray-900">Documents</h3>
        {can.editQuestionnaire() && hasQuestionnaire && (
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkProcess}
                disabled={isCompleted}
                title={isCompleted ? 'Reopen questionnaire to reprocess' : undefined}
                className="btn btn-primary btn-sm"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Process ({selectedIds.size})
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn btn-primary btn-sm"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_EXTENSIONS.join(',')}
              onChange={handleUpload}
              className="hidden"
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-gray-500 py-4 text-center">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No documents uploaded yet</p>
        </div>
      ) : (
        <>
          {/* Selection toolbar */}
          {can.editQuestionnaire() && (
            <div className="flex items-center gap-4 mb-2 px-1">
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleToggleAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                All
              </label>
              {unprocessedIds.size > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={unprocessedSelected}
                    onChange={handleToggleUnprocessed}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Unprocessed ({unprocessedIds.size})
                </label>
              )}
            </div>
          )}

          <div className="space-y-2">
            {documents.map((doc) => {
              const badge = STATUS_BADGES[doc.processing_status] || STATUS_BADGES.pending;
              const isActive = ACTIVE_STATUSES.includes(doc.processing_status);
              const errorMsg = reprocessErrors[doc.id];

              return (
                <div
                  key={doc.id}
                  className={`flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg transition-opacity ${isActive ? 'opacity-70' : ''}`}
                >
                  {can.editQuestionnaire() && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(doc.id)}
                      onChange={() => handleToggleRow(doc.id)}
                      onClick={e => e.stopPropagation()}
                      disabled={isActive}
                      className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 disabled:opacity-40"
                    />
                  )}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                      <p className="text-xs text-gray-500">{formatSize(doc.size_bytes)} &middot; {formatDate(doc.created_at)}</p>
                      {/* last_processed_at display — processing state wins */}
                      {isActive ? (
                        <p className="text-xs text-yellow-600 flex items-center gap-1 mt-0.5">
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Processing...
                        </p>
                      ) : errorMsg ? (
                        <p className="text-xs text-red-500 mt-0.5" title={errorMsg}>Reprocess failed</p>
                      ) : doc.last_processed_at ? (
                        <p className="text-xs text-gray-400 mt-0.5">Last processed: {formatLastProcessed(doc.last_processed_at)}</p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-0.5">Not yet processed</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                    {doc.processing_result?.proposals_created > 0 && (
                      <span className="text-xs text-blue-600 font-medium">
                        {doc.processing_result.proposals_created} changes
                      </span>
                    )}
                    {doc.download_url && (
                      <a href={doc.download_url} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-blue-600 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                    )}
                    {can.editQuestionnaire() && (
                      <button
                        onClick={(e) => handleReprocess(doc.id, e)}
                        disabled={isActive || isCompleted}
                        title={isCompleted ? 'Reopen questionnaire to reprocess' : isActive ? 'Processing...' : 'Reprocess document'}
                        className="text-gray-400 hover:text-blue-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isActive ? (
                          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>
                    )}
                    {can.editQuestionnaire() && (
                      <button onClick={() => handleDelete(doc.id)} disabled={isActive} className="text-gray-400 hover:text-red-600 transition disabled:opacity-40">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {documents.some(d => d.processing_status === 'failed') && (
        <div className="mt-2 p-2 bg-red-50 rounded text-[10px] text-red-700">
          Some documents failed processing. Check the error details or try re-uploading.
        </div>
      )}
    </div>
  );
};

export default DocumentsCard;
